import { useMemo, useState } from 'react'
import {
  FEATURES,
  FEATURE_GROUPS,
  featureApplies,
  runAblation,
  type AblationMetric,
  type AblationRow,
  type FeatureKey,
  type FeatureSpec,
} from '@/domain/hs1'
import { Alert, Badge, Button, Card, Switch, Table, Td, Th } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, num } from '@/sim/parts'

/**
 * Merkmale — fünfzehn Modellstufen, einzeln zuschaltbar.
 *
 * WOZU DIESE SEITE DA IST. Ein Modell mit fünfzehn Anteilen liefert eine Zahl,
 * und die Zahl sagt nicht, woher sie kommt. Nimmt man einen Anteil heraus und
 * die Zahl bleibt stehen, war er in diesem Betriebsfall entbehrlich; bewegt sie
 * sich um dreissig Prozent, ist er die halbe Aussage. Das ist der einzige Weg,
 * ein zusammengesetztes Modell zu verstehen, ohne es zu zerlegen.
 *
 * DIE WIRKUNGSÜBERSICHT ist der eigentliche Inhalt. Sie legt jede Stufe einzeln
 * um, lässt alles andere stehen und trägt die Änderung ein. Fünfzehn zusätzliche
 * Durchläufe des Kerns — deshalb auf Knopfdruck und nicht bei jedem Klick.
 *
 * WAS DIE SEITE NICHT VERSCHWEIGT: zwei Stufen wirken nur auf den
 * aufgezeichneten Gang, eine nur auf den gerechneten. Das steht an jedem
 * Schalter, weil sonst genau der Verdacht entsteht, den diese Seite ausräumen
 * soll — dass ein Schalter nichts tut.
 */

export function Features() {
  const { model, state, simulation, simulate, setFeature } = useSimulation()
  const [ablation, setAblation] = useState<ReturnType<typeof runAblation> | null>(null)
  const [busy, setBusy] = useState(false)
  const [sortByWeight, setSortByWeight] = useState(true)

  const on = FEATURES.filter((f) => state.features[f.key]).length
  /* Die Stufen, die in DIESER Betriebsart nichts wegzunehmen haben. */
  const idle = FEATURES.filter((f) => !featureApplies(f, state.generated))
  const inactive = idle.length

  /*
    Die Übersicht wird bei jeder Änderung ungültig — sie ist ein Vergleich GEGEN
    den aktuellen Zustand, und wenn der sich ändert, vergleicht sie gegen einen
    Zustand, den es nicht mehr gibt. Lieber leer als veraltet.
  */
  const stamp = useMemo(() => JSON.stringify([model.id, state]), [model.id, state])
  const [stampAt, setStampAt] = useState('')
  const stale = ablation !== null && stampAt !== stamp

  const compute = () => {
    setBusy(true)
    /* Ein Moment Luft, damit der Knopf seinen Zustand zeigt, bevor der Rechner
       fünfzehn Durchläufe lang nicht mehr zeichnet. */
    window.setTimeout(() => {
      setAblation(
        runAblation(
          (key) =>
            simulate({ ...state, features: { ...state.features, [key]: !state.features[key] } }),
          simulation,
        ),
      )
      setStampAt(stamp)
      setBusy(false)
    }, 30)
  }

  const rows = useMemo(() => {
    if (!ablation) return []
    const list = [...ablation.rows]
    if (sortByWeight) list.sort((a, b) => b.weight - a.weight)
    return list
  }, [ablation, sortByWeight])

  return (
    <div className="stack">
      <p className="page-lead">
        Jede Stufe ist ein Anteil des Modells, den man herausnehmen kann. Die Wirkungsübersicht
        legt jede Stufe einzeln um und trägt ein, was sich dabei ändert.
      </p>

      <div className="metrics">
        <Metric label="Eingeschaltet" value={`${on} von ${FEATURES.length}`} />
        <Metric
          label="Betriebsart"
          value={state.generated ? 'gerechnet' : 'aufgezeichnet'}
          note={
            inactive === 0
              ? 'alle Stufen wirksam'
              : inactive === 1
                ? 'eine Stufe wirkt hier nicht'
                : `${inactive} Stufen wirken hier nicht`
          }
        />
        <Metric
          label="Nettoarbeit je Zyklus"
          value={`${num(simulation.power.work, 0)} J`}
          note={`davon ${num(simulation.power.holdWork, 0)} J Halten und Grundlast`}
        />
        <Metric
          label="Rückgewinnung"
          value={`${num(simulation.metrics.recovered * 100, 0)} %`}
          note={`Feder ${num(simulation.power.fromSpring, 0)} J · Zwischenkreis ${num(simulation.power.fromBus, 0)} J`}
        />
      </div>

      {inactive > 0 && (
        <Alert
          tone="info"
          title={
            inactive === 1
              ? `${idle[0].label} hat in dieser Betriebsart nichts zu tun`
              : `${inactive} Stufen haben in dieser Betriebsart nichts zu tun`
          }
        >
          {state.generated ? (
            <>
              Der gerechnete Gang hat diese Anteile nicht: seine Beckenkippung steht fest und
              schwingt nicht, und das Zehengrundgelenk bekommt keine Winkelvorgabe — ein starrer
              Fuss ist bereits der Zustand. Es gibt dort nichts wegzunehmen.
            </>
          ) : (
            <>
              Der aufgezeichnete Gang setzt den Fuss nicht, er liest Gelenkwinkel aus Normkurven ab;
              wo der Fuss dabei landet, ist Ergebnis und nicht Vorgabe. Eine Abduktorenführung, die
              man abschalten könnte, gibt es dort nicht.
            </>
          )}{' '}
          Das ist kein Defekt, sondern der Unterschied zwischen den beiden Betriebsarten. Betroffen:{' '}
          {idle.map((f) => f.label).join(', ')}. Die Schalter bleiben bedienbar — in der anderen
          Betriebsart wirken sie.
        </Alert>
      )}

      {FEATURE_GROUPS.map((group) => (
        <Card key={group} title={group} eyebrow={`${countOn(group, state.features)} aktiv`}>
          <div className="feat">
            {FEATURES.filter((f) => f.group === group).map((spec) => (
              <FeatureRow
                key={spec.key}
                spec={spec}
                on={state.features[spec.key]}
                applies={featureApplies(spec, state.generated)}
                onToggle={(value) => setFeature(spec.key, value)}
              />
            ))}
          </div>
        </Card>
      ))}

      <Card
        title="Wirkung der Modellstufen"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {ablation && (
              <Button size="sm" variant="ghost" onClick={() => setSortByWeight((v) => !v)}>
                {sortByWeight ? 'nach Gruppe' : 'nach Wirkung'}
              </Button>
            )}
            <Button size="sm" variant="primary" onClick={compute} disabled={busy}>
              {busy ? 'rechnet …' : ablation ? 'Neu berechnen' : 'Wirkung berechnen'}
            </Button>
          </div>
        }
      >
        {!ablation ? (
          <p className="page-lead">
            Noch nicht gerechnet. Der Durchlauf legt jede der fünfzehn Stufen einzeln um und rechnet
            den Kern dabei fünfzehnmal durch — das dauert einen Moment und geschieht deshalb nur auf
            Knopfdruck.
          </p>
        ) : (
          <div className="stack stack--tight">
            {stale && (
              <Alert tone="warning" title="Nicht mehr aktuell">
                Seit dem Durchlauf hat sich der Zustand geändert. Die Tabelle vergleicht gegen einen
                Ausgangszustand, den es nicht mehr gibt — neu berechnen.
              </Alert>
            )}
            <Table className="feat-table">
              <thead>
                <tr>
                  <Th>Modellstufe</Th>
                  <Th>Umgelegt auf</Th>
                  {ablation.metrics.map((metric) => (
                    <Th key={metric.key} numeric>
                      {metric.label}
                      {metric.unit && <small> {metric.unit}</small>}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="feat__base">
                  <Td>
                    <b>Ausgangszustand</b>
                  </Td>
                  <Td>—</Td>
                  {ablation.metrics.map((metric) => (
                    <Td key={metric.key} numeric>
                      {num(ablation.base[metric.key], metric.digits)}
                    </Td>
                  ))}
                </tr>
                {rows.map((row) => (
                  <AblationLine key={row.spec.key} row={row} metrics={ablation.metrics} />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <Card title="Lesehinweise">
        <div className="balance__notes">
          <div>
            <span className="eyebrow">Umgelegt, nicht ausgeschaltet</span>
            <p className="page-lead">
              Die Tabelle zeigt immer den Weg zum ANDEREN Zustand. In der Spalte daneben steht,
              worauf umgelegt wurde.
            </p>
          </div>
          <div>
            <span className="eyebrow">Der Federspeicher kostet mehr, als er bringt</span>
            <p className="page-lead">
              Nettoarbeit je Zyklus mit ihm gegen ohne ihn: Gehen 97 zu 88 J, Hackensprung 520 zu
              466 J. Er kostet in jeder Bewegung, in der er überhaupt etwas ändert — auch beim
              Sprung, wo Aufnahme und Abgabe dicht beieinanderliegen und eine Sehne sich lohnen
              müsste. Der Grund ist nicht seine Zeitkonstante, sondern die Reihenfolge: er greift
              zuerst zu und nimmt 55 % der Bremsleistung, gibt sie zu 80 % und nur im eigenen Gelenk
              zurück. Dieselbe Leistung ginge sonst auf den Zwischenkreis, der nicht zerfällt und
              aus dem jedes Gelenk zu jedem späteren Zeitpunkt schöpfen kann. Für sich genommen ist
              er gut — beim Sprung holt er 270 J zurück; er verliert nur das Rennen gegen einen
              Speicher, der nichts verliert.
            </p>
          </div>
          <div>
            <span className="eyebrow">Was hier nicht steht</span>
            <p className="page-lead">
              Wechselwirkungen. Die Übersicht legt jede Stufe für sich um; zwei zusammen können mehr
              oder weniger bewirken als die Summe. Der Federspeicher ist genau so ein Fall: allein
              spart er, neben dem Zwischenkreis kostet er. Wer solche Paare sucht, schaltet von Hand
              und rechnet neu.
            </p>
          </div>
          <div>
            <span className="eyebrow">Sortiert nach Wirkung, relativ</span>
            <p className="page-lead">
              Das Gewicht einer Zeile ist die grösste RELATIVE Änderung über alle Kennzahlen.
              Absolut verglichen stünden 40 Nm und 40 J gleich schwer in der Liste.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}

const countOn = (group: string, features: Record<FeatureKey, boolean>): string => {
  const list = FEATURES.filter((f) => f.group === group)
  return `${list.filter((f) => features[f.key]).length} von ${list.length}`
}

function FeatureRow({
  spec,
  on,
  applies,
  onToggle,
}: {
  spec: FeatureSpec
  on: boolean
  applies: boolean
  onToggle: (value: boolean) => void
}) {
  return (
    <div className={['feat__row', on ? '' : 'feat__row--off'].filter(Boolean).join(' ')}>
      <Switch
        checked={on}
        onChange={(e) => onToggle(e.currentTarget.checked)}
        label={
          <span className="feat__head">
            <b>{spec.label}</b>
            {!applies && (
              <Badge tone="neutral">
                wirkt nur {spec.scope === 'gerechnet' ? 'gerechnet' : 'aufgezeichnet'}
              </Badge>
            )}
          </span>
        }
      />
      <p className="feat__note">
        {spec.note} <span className="feat__off">Aus: {spec.off}</span>
      </p>
    </div>
  )
}

function AblationLine({ row, metrics }: { row: AblationRow; metrics: AblationMetric[] }) {
  return (
    <tr className={row.applies ? undefined : 'feat__inert'}>
      <Td>
        {row.spec.label}
        <small className="feat__group"> {row.spec.group}</small>
      </Td>
      <Td>{row.was ? 'aus' : 'an'}</Td>
      {/*
        DIE ÄNDERUNG WIRD NICHT EINGEFÄRBT, und das ist Absicht. Bei elf
        Kennzahlen zeigt „mehr" mal nach oben und mal nach unten: eine höhere
        Geschwindigkeit ist ein Gewinn, eine höhere Bodenkraft eine Last, eine
        höhere Rückgewinnung ein Gewinn, eine höhere Nettoarbeit eine Last. Wer
        Zunahme durchgängig rot färbt, behauptet eine Bewertung, die für die
        Hälfte der Spalten falsch ist. Vorzeichen genügt.
      */}
      {metrics.map((metric) => {
        const delta = row.delta[metric.key]
        /* Unter einer halben Anzeigestelle ist keine Änderung, sondern
           Rundungsrauschen — als Änderung gezeigt wäre es eine Behauptung. */
        const shown = Math.abs(delta) >= 0.5 * Math.pow(10, -metric.digits)
        return (
          <Td key={metric.key} numeric>
            <span className="feat__value">{num(row.values[metric.key], metric.digits)}</span>
            {shown && (
              <span className="feat__delta">
                {delta > 0 ? '+' : '−'}
                {num(Math.abs(delta), metric.digits)}
              </span>
            )}
          </Td>
        )
      })}
    </tr>
  )
}
